"use client";

import { useId, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import type { DeliveryAttempt } from "../../../services/merchantApi";
import { CodeBlock, TimeText, focusRing } from "../ui";
import { formatAbsolute, formatDuration, formatUtc } from "./format";
import { ATTEMPT_OUTCOME_UI, fallbackHint } from "./vocab";
import { HttpStatusChip } from "./HttpStatusChip";

// ────────────────────────────────────────────────────────────────────────────
// AttemptTimeline — every attempt of one delivery, newest first, as a
// vertical timeline. Used by DeliveryDetailPage (and optionally by the test
// modal's "full result").
//
// Everything in an attempt that came from the merchant's server — error
// text, response headers, response body — is rendered as React text
// children only. The response body goes through CodeBlock (JSON colouring
// emits React nodes, never HTML) with wrapping on, because the app hides
// scrollbars and nothing may rely on horizontal scrolling.
// ────────────────────────────────────────────────────────────────────────────

/**
 * @param attempts    `delivery.attempts` in the order the API returns them
 *                    (chronological); displayed newest first
 * @param maxAttempts `delivery.maxAttempts` — prints "Attempt 3 of 6"
 */
export default function AttemptTimeline({
  attempts,
  maxAttempts,
}: {
  attempts: DeliveryAttempt[];
  maxAttempts: number;
}) {
  // Newest first. An interrupted stub carries the same `number` as the real
  // attempt that follows it, so order by start time, then by position.
  const ordered = useMemo(
    () =>
      (attempts || [])
        .map((attempt, index) => ({ attempt, index }))
        .sort((x, y) => {
          const tx = Date.parse(x.attempt.startedAt);
          const ty = Date.parse(y.attempt.startedAt);
          if (!Number.isNaN(tx) && !Number.isNaN(ty) && tx !== ty) return ty - tx;
          return y.index - x.index;
        }),
    [attempts],
  );

  if (ordered.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No attempts yet. The first one appears here as soon as it is made.
      </p>
    );
  }

  return (
    <ol className="pt-1">
      {ordered.map(({ attempt, index }, i) => (
        <AttemptItem
          key={`${attempt.number}-${index}`}
          attempt={attempt}
          maxAttempts={maxAttempts}
          newest={i === 0}
          last={i === ordered.length - 1}
        />
      ))}
    </ol>
  );
}

function AttemptItem({
  attempt: a,
  maxAttempts,
  newest,
  last,
}: {
  attempt: DeliveryAttempt;
  maxAttempts: number;
  newest: boolean;
  last: boolean;
}) {
  const ui = ATTEMPT_OUTCOME_UI[a.outcome] ?? ATTEMPT_OUTCOME_UI.failed;
  const interrupted = a.outcome === "interrupted";
  const failed = a.outcome === "failed";
  const hint = failed ? a.hint || fallbackHint(a.errorCode, a.responseStatus) : null;
  const headerEntries = a.responseHeaders ? Object.entries(a.responseHeaders) : [];
  const hasSnippet = typeof a.responseSnippet === "string" && a.responseSnippet.length > 0;
  const gotResponse = typeof a.responseStatus === "number" && a.responseStatus > 0;

  // The newest failed response is the thing a developer came here to read,
  // so it starts open; everything else starts collapsed.
  const [showHeaders, setShowHeaders] = useState(false);
  const [showBody, setShowBody] = useState(newest && failed && hasSnippet);
  const headersId = useId();
  const bodyId = useId();

  const signedWith = (a.signedWith || [])
    .map((s) => `••••${s.last4} (v${s.version})`)
    .join(", ");

  return (
    <li className={`relative pl-6 border-l ${last ? "border-transparent" : "border-gray-800"} ${last ? "" : "pb-7"}`}>
      <span
        aria-hidden="true"
        className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ring-4 ring-[#0D1117] ${ui.dot}`}
      />

      {/* Header: Attempt 3 of 6 · Attempt failed · HTTP 500 · 204 ms · Sep 18, 09:50:12 */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 -mt-0.5">
        <span className="text-sm font-semibold text-white">
          Attempt {a.number}
          {maxAttempts > 1 && !interrupted && (
            <span className="font-normal text-gray-500"> of {maxAttempts}</span>
          )}
        </span>
        <Dot />
        <span className={`text-sm font-semibold ${ui.text}`}>{ui.label}</span>
        {!interrupted && (gotResponse || a.errorCode) && (
          <HttpStatusChip status={a.responseStatus} errorCode={a.errorCode} />
        )}
        {a.durationMs != null && !interrupted && (
          <>
            <Dot />
            <span className="text-xs text-gray-400 tabular-nums">{formatDuration(a.durationMs)}</span>
          </>
        )}
        <Dot />
        <TimeText iso={a.startedAt} initial="absolute" className="text-xs text-gray-500" />
      </div>

      {/* What happened + how to fix it. */}
      {interrupted ? (
        <p className="mt-2 text-sm text-gray-400 leading-relaxed">{ui.detail}</p>
      ) : (
        a.errorMessage && (
          <p className="mt-2 text-sm text-gray-200 leading-relaxed break-words">{a.errorMessage}</p>
        )
      )}
      {hint && <p className="mt-1.5 text-xs text-gray-400 leading-relaxed break-words">{hint}</p>}
      {failed && a.retryAfterSeconds != null && a.retryAfterSeconds > 0 && (
        <p className="mt-1.5 text-xs text-gray-500">
          Your server asked us to wait {a.retryAfterSeconds}s (Retry-After); the next attempt honours it
          up to 1 hour.
        </p>
      )}

      {/* Key facts. dt/dd stack on phones, two columns from sm up. */}
      {!interrupted && (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-[8.5rem_minmax(0,1fr)] gap-x-4 gap-y-1 sm:gap-y-1.5 text-xs">
          <Fact label="Started">
            <span className="text-gray-200 whitespace-nowrap">{formatAbsolute(a.startedAt)}</span>{" "}
            <span className="text-gray-500 whitespace-nowrap">· UTC {formatUtc(a.startedAt)}</span>
          </Fact>
          {a.url && (
            <Fact label="Sent to" mono>
              {a.url}
            </Fact>
          )}
          <Fact label="Resolved IP" mono>
            {a.resolvedIp || <span className="font-sans text-gray-500">Not connected</span>}
          </Fact>
          {a.signature && (
            <Fact label="Signature" mono>
              {a.signature}
            </Fact>
          )}
          {signedWith && <Fact label="Signed with" mono>{signedWith}</Fact>}
        </dl>
      )}

      {/* Response — collapsible headers table and body. */}
      {!interrupted && (
        <div className="mt-3">
          {headerEntries.length > 0 || hasSnippet ? (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              {headerEntries.length > 0 && (
                <Disclosure
                  open={showHeaders}
                  onToggle={() => setShowHeaders((v) => !v)}
                  controls={headersId}
                >
                  Response headers ({headerEntries.length})
                </Disclosure>
              )}
              {hasSnippet && (
                <Disclosure open={showBody} onToggle={() => setShowBody((v) => !v)} controls={bodyId}>
                  Response body
                </Disclosure>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              {gotResponse ? "Empty response body." : "No response was received from your server."}
            </p>
          )}

          {showHeaders && headerEntries.length > 0 && (
            <div id={headersId} className="mt-2 rounded-xl border border-gray-800 overflow-hidden">
              <table className="w-full table-fixed text-xs">
                <thead className="bg-gray-900/60 text-gray-400">
                  <tr>
                    <th scope="col" className="w-2/5 sm:w-1/3 text-left px-3 py-2 font-semibold">
                      Header
                    </th>
                    <th scope="col" className="text-left px-3 py-2 font-semibold">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/70">
                  {headerEntries.map(([name, value]) => (
                    <tr key={name}>
                      <td className="px-3 py-2 align-top font-mono text-gray-400 break-all">{name}</td>
                      <td className="px-3 py-2 align-top font-mono text-gray-200 break-all">{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showBody && hasSnippet && (
            <div id={bodyId}>
              <CodeBlock
                language="response"
                code={a.responseSnippet as string}
                highlight
                wrap
                collapsedMaxLines={20}
              />
              <p className="-mt-1 text-[11px] text-gray-500">
                {a.responseTruncated
                  ? "First 2,000 characters of your server's response (truncated)."
                  : "Your server's complete response body."}
              </p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function Dot() {
  return (
    <span aria-hidden="true" className="text-gray-600 text-xs">
      ·
    </span>
  );
}

function Fact({ label, mono = false, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-gray-500 mt-1.5 sm:mt-0">{label}</dt>
      <dd className={`min-w-0 ${mono ? "break-all font-mono text-[11.5px] text-gray-200" : "break-words"}`}>{children}</dd>
    </>
  );
}

function Disclosure({
  open,
  onToggle,
  controls,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  controls: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className={`inline-flex items-center gap-1 py-1.5 text-xs font-medium text-gray-400 hover:text-white transition-colors rounded-md ${focusRing}`}
    >
      <ChevronRight
        className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`}
        aria-hidden="true"
      />
      {children}
    </button>
  );
}
